import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { apiError } from "@/lib/api";
import { money } from "@/lib/format";
import { toast } from "sonner";

const inputCls = "bg-[#1E222D] border-[#2A2E39] text-[#EAECEF] placeholder:text-[#5E6673] focus-visible:ring-[#F0B90B]/50";
const labelCls = "text-[11px] font-medium uppercase tracking-wider text-[#848E9C]";

export default function FundsDialog({ target, onClose, onDone }) {
  const [mode, setMode] = useState("deposit");
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("Bank Transfer");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (target) {
      setMode(target.mode);
      setAmount("");
      setNote("");
      setMethod("Bank Transfer");
    }
  }, [target]);

  const submit = async () => {
    const val = parseFloat(amount);
    if (!val || val <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    setLoading(true);
    try {
      await api.post(`/admin/clients/${target.client.id}/funds`, { type: mode, amount: val, method, note });
      toast.success(`${mode === "deposit" ? "Deposit" : "Withdrawal"} of ${money(val)} completed for ${target.client.name}`);
      onClose();
      onDone?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="deposit-withdraw-modal" className="border-[#2A2E39] bg-[#131722] text-[#EAECEF] sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">Manage Funds</DialogTitle>
          <DialogDescription className="text-sm text-[#848E9C]">
            Deposit or withdraw funds for this client. Balances update instantly.
          </DialogDescription>
        </DialogHeader>
        <div className="mb-1 flex items-center justify-between rounded-md border border-[#2A2E39] bg-[#1E222D] px-3 py-2">
          <span className="text-sm text-[#EAECEF]">{target?.client?.name}</span>
          <span className="font-mono text-sm text-[#F0B90B]" data-testid="funds-current-balance">{money(target?.client?.balance ?? 0)}</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button
            data-testid="fund-action-deposit-btn"
            onClick={() => setMode("deposit")}
            className={`rounded-md py-2 text-sm font-bold tracking-wide transition-colors ${
              mode === "deposit" ? "bg-[#0ECB81] text-black" : "border border-[#2A2E39] bg-[#1E222D] text-[#848E9C] hover:bg-[#2A2E39]"
            }`}
          >
            DEPOSIT
          </button>
          <button
            data-testid="fund-action-withdraw-btn"
            onClick={() => setMode("withdrawal")}
            className={`rounded-md py-2 text-sm font-bold tracking-wide transition-colors ${
              mode === "withdrawal" ? "bg-[#F6465D] text-white" : "border border-[#2A2E39] bg-[#1E222D] text-[#848E9C] hover:bg-[#2A2E39]"
            }`}
          >
            WITHDRAW
          </button>
        </div>
        <div className="space-y-1.5">
          <Label className={labelCls}>Amount (USD)</Label>
          <Input data-testid="fund-amount-input" type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className={`${inputCls} font-mono`} />
        </div>
        <div className="space-y-1.5">
          <Label className={labelCls}>Payment Method</Label>
          <select
            data-testid="fund-method-select"
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            className="w-full rounded-md border border-[#2A2E39] bg-[#1E222D] px-3 py-2 text-sm text-[#EAECEF] focus:outline-none focus:ring-1 focus:ring-[#F0B90B]/50"
          >
            <option>Bank Transfer</option>
            <option>Wire Transfer</option>
            <option>USDT (TRC20)</option>
            <option>USDT (ERC20)</option>
            <option>Cash</option>
            <option>Other</option>
          </select>
        </div>
        <div className="space-y-1.5">
          <Label className={labelCls}>Note (optional)</Label>
          <Input data-testid="fund-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Monthly top-up" className={inputCls} />
        </div>
        <button
          data-testid="submit-fund-transaction-btn"
          onClick={submit}
          disabled={loading}
          className={`w-full rounded-md py-2.5 text-sm font-semibold transition-colors disabled:opacity-50 ${
            mode === "deposit" ? "bg-[#0ECB81] text-black hover:bg-[#10DC8C]" : "bg-[#F6465D] text-white hover:bg-[#FF536A]"
          }`}
        >
          {loading ? "Processing..." : mode === "deposit" ? "Confirm Deposit" : "Confirm Withdrawal"}
        </button>
      </DialogContent>
    </Dialog>
  );
}
